/**
 * Inline SVG icons for Header, Cart, Checkout & PDP — replaces lucide-react
 * imports to remove the ~62KB vendor-icons chunk from the critical path
 * across all key conversion pages.
 *
 * All icons match lucide-react's default viewBox (24x24) and stroke style
 * so they are drop-in compatible (same className, size & strokeWidth props).
 */

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const defaults: IconProps = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export const SearchIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
);

export const UserIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
);

export const HeartIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
);

export const ShoppingBagIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
);

export const ShoppingCartIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>
);

export const XIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
);

export const TruckIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 13.52 8H12"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
);

export const CreditCardIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><rect width="20" height="14" x="2" y="5" rx="2"/><path d="M2 10h20"/></svg>
);

export const HeadphonesIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"/></svg>
);

export const ArrowRightIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
);

export const ArrowLeftIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
);

export const StarIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
);

export const RotateCcwIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
);

export const ChevronRightIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="m9 18 6-6-6-6"/></svg>
);

export const ChevronLeftIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="m15 18-6-6 6-6"/></svg>
);

export const LockIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
);

export const ShieldIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>
);

export const Trash2Icon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
);

export const PlusIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M5 12h14"/><path d="M12 5v14"/></svg>
);

export const MinusIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M5 12h14"/></svg>
);

export const SparklesIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/></svg>
);

export const ClockIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
);

export const PackageIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>
);

export const CheckIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M20 6 9 17l-5-5"/></svg>
);

export const Loader2Icon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
);

export const DogIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M11.25 16.25h1.5L12 17z"/><path d="M16 14v.5"/><path d="M4.42 11.247A13.152 13.152 0 0 0 4 14.556C4 18.728 7.582 21 12 21s8-2.272 8-6.444a11.702 11.702 0 0 0-.493-3.309"/><path d="M8 14v.5"/><path d="M8.5 8.5c-.384 1.05-1.083 2.028-2.344 2.5-1.931.722-3.576-.297-3.656-1-.113-.994 1.177-6.53 4-7 1.923-.321 3.651.845 3.651 2.235A7.497 7.497 0 0 1 14 5c0-1.39 1.844-2.598 3.767-2.277 2.823.47 4.113 6.006 4 7-.08.703-1.725 1.722-3.656 1-1.261-.472-1.96-1.45-2.344-2.5"/></svg>
);

export const CatIcon = (props: IconProps) => (
  <svg {...defaults} {...props}><path d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3-9-7.56c0-1.25.5-2.4 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5Z"/><path d="M8 14v.5"/><path d="M16 14v.5"/><path d="M11.25 16.25h1.5L12 17l-.75-.75Z"/></svg>
);
